/**
 * Custom authentication service for Email OTP passwordless authentication
 * Handles sign-in flow with email OTP challenge
 */

import { Amplify } from 'aws-amplify';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { saveEmail, saveUsername } from '../utils/emailStorage';

/**
 * Sign in with email OTP (passwordless)
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Sign-in result with next step information
 */
/**
 * Sign in with username + password (Cognito USER_PASSWORD_AUTH).
 * Requires an app client that allows ALLOW_USER_PASSWORD_AUTH.
 */
export const signInWithUserPassword = async (username, password) => {
  const u = typeof username === 'string' ? username.trim() : username;
  if (process.env.NODE_ENV === 'development') {
    const c = Amplify.getConfig().Auth?.Cognito;
    console.info('[auth] signIn USER_PASSWORD_AUTH using:', {
      userPoolId: c?.userPoolId,
      userPoolClientId: c?.userPoolClientId,
      authenticationFlowType: c?.authenticationFlowType,
    });
  }
  try {
    const result = await signIn({
      username: u,
      password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });

    if (u) {
      saveUsername(u);
      saveEmail(u); // also save as email in case user switches to OTP mode
    }

    return result;
  } catch (error) {
    console.error('Error signing in with password:', error);
    if (process.env.NODE_ENV === 'development') {
      const c = Amplify.getConfig().Auth?.Cognito;
      console.info(
        '[auth] If credentials are correct in CLI, ensure userPoolId + userPoolClientId match that client in Cognito (optional REACT_APP_COGNITO_USER_POOL_ID). Runtime:',
        { userPoolId: c?.userPoolId, userPoolClientId: c?.userPoolClientId }
      );
    }
    throw error;
  }
};

export const signInWithEmailOTP = async (email) => {
  try {
    const result = await signIn({
      username: email,
      options: {
        authFlowType: 'USER_AUTH',
        preferredChallenge: 'EMAIL_OTP',
      },
    });

    // Save email for future logins
    if (email) {
      saveEmail(email);
    }

    return result;
  } catch (error) {
    console.error('Error signing in with Email OTP:', error);
    throw error;
  }
};

/**
 * Confirm sign-in with OTP code
 * @param {string} code - OTP code from email
 * @returns {Promise<Object>} Confirmation result
 */
export const confirmSignInWithOTP = async (code) => {
  try {
    const result = await confirmSignIn({
      challengeResponse: code,
    });

    return result;
  } catch (error) {
    console.error('Error confirming sign-in with OTP:', error);
    throw error;
  }
};

/**
 * No-op: credentials are remembered across sessions.
 * Callers that previously used this to wipe data can still import it.
 */
export const clearAuthData = () => {
  // intentionally empty — we keep credentials in localStorage
};

